/**
 * Smart Tempo - Logic Pro Inspired
 * Automatically detects tempo from audio using onset detection and beat tracking
 * Uses Web Audio API for real-time audio analysis
 */

/**
 * Analyze audio buffer to detect tempo
 * Uses onset detection with autocorrelation for tempo estimation
 * @param {AudioBuffer} audioBuffer - The audio buffer to analyze
 * @param {AudioContext} audioContext - The audio context
 * @returns {Promise<{tempo: number, confidence: number, beats: number[]}>}
 */
export const detectTempo = async (audioBuffer, audioContext) => {
  return new Promise((resolve) => {
    // Get mono audio data
    const channelData = audioBuffer.getChannelData(0);
    const sampleRate = audioBuffer.sampleRate;
    
    // Parameters for onset detection
    const hopSize = 512; // ~11.6ms at 44100Hz
    const frameSize = 2048;
    
    // Calculate spectral flux for onset detection
    const onsets = detectOnsets(channelData, sampleRate, frameSize, hopSize);
    
    // Find peaks in onset function (beat candidates)
    const peaks = findPeaks(onsets, hopSize, sampleRate);
    
    // Estimate tempo using inter-onset intervals
    const { tempo, confidence } = estimateTempo(peaks, sampleRate);
    
    // Refine beat positions based on estimated tempo
    const beats = refineBeatGrid(peaks, tempo, sampleRate);
    
    resolve({
      tempo: Math.round(tempo),
      confidence: Math.round(confidence * 100) / 100,
      beats,
      onsets: peaks,
    });
  });
};

/**
 * Detect onsets using spectral flux
 */
const detectOnsets = (channelData, sampleRate, frameSize, hopSize) => {
  const numFrames = Math.floor((channelData.length - frameSize) / hopSize);
  const onsets = new Float32Array(numFrames);
  
  let prevMagnitudes = new Float32Array(frameSize / 2);
  
  for (let i = 0; i < numFrames; i++) {
    const startSample = i * hopSize;
    const frame = channelData.slice(startSample, startSample + frameSize);
    
    // Apply Hanning window
    const windowed = applyWindow(frame);
    
    // Compute magnitude spectrum using DFT approximation
    const magnitudes = computeMagnitudeSpectrum(windowed);
    
    // Compute spectral flux (positive only)
    let flux = 0;
    for (let j = 0; j < magnitudes.length; j++) {
      const diff = magnitudes[j] - prevMagnitudes[j];
      if (diff > 0) flux += diff;
    }
    
    onsets[i] = flux;
    prevMagnitudes = magnitudes;
  }
  
  // Normalize
  const maxOnset = Math.max(...onsets);
  if (maxOnset > 0) {
    for (let i = 0; i < onsets.length; i++) {
      onsets[i] /= maxOnset;
    }
  }
  
  return onsets;
};

/**
 * Apply Hanning window to frame
 */
const applyWindow = (frame) => {
  const windowed = new Float32Array(frame.length);
  for (let i = 0; i < frame.length; i++) {
    const window = 0.5 * (1 - Math.cos(2 * Math.PI * i / (frame.length - 1)));
    windowed[i] = frame[i] * window;
  }
  return windowed;
};

/**
 * Compute magnitude spectrum using simplified DFT
 * (Optimized for tempo detection, not full FFT accuracy)
 */
const computeMagnitudeSpectrum = (frame) => {
  const N = frame.length;
  const halfN = N / 2;
  const magnitudes = new Float32Array(halfN);
  
  // Compute simplified energy in frequency bands
  const numBands = 32;
  const bandSize = Math.floor(halfN / numBands);
  
  for (let band = 0; band < numBands; band++) {
    let energy = 0;
    const startIdx = band * bandSize;
    const endIdx = Math.min(startIdx + bandSize, halfN);
    
    // Sum squared samples in band (approximation)
    for (let i = startIdx; i < endIdx; i++) {
      const sampleIdx = Math.floor(i * N / halfN);
      if (sampleIdx < frame.length) {
        energy += frame[sampleIdx] * frame[sampleIdx];
      }
    }
    
    magnitudes[band] = Math.sqrt(energy);
  }
  
  return magnitudes;
};

/**
 * Find peaks in onset detection function
 */
const findPeaks = (onsets, hopSize, sampleRate) => {
  const peaks = [];
  const threshold = 0.3; // Minimum onset strength
  const minPeakDistance = Math.floor(sampleRate / hopSize * 0.1); // 100ms minimum
  
  for (let i = 2; i < onsets.length - 2; i++) {
    // Check if local maximum
    if (onsets[i] > threshold &&
        onsets[i] > onsets[i - 1] &&
        onsets[i] > onsets[i - 2] &&
        onsets[i] > onsets[i + 1] &&
        onsets[i] > onsets[i + 2]) {
      
      // Check minimum distance from last peak
      if (peaks.length === 0 || i - peaks[peaks.length - 1].frame >= minPeakDistance) {
        peaks.push({
          frame: i,
          time: (i * hopSize) / sampleRate,
          strength: onsets[i],
        });
      }
    }
  }
  
  return peaks;
};

/**
 * Estimate tempo from peak intervals using histogram
 */
const estimateTempo = (peaks, sampleRate) => {
  if (peaks.length < 4) {
    return { tempo: 120, confidence: 0 };
  }
  
  // Calculate inter-onset intervals
  const intervals = [];
  for (let i = 1; i < peaks.length; i++) {
    const interval = peaks[i].time - peaks[i - 1].time;
    if (interval > 0.2 && interval < 2.0) { // Between 30 and 300 BPM
      intervals.push(interval);
    }
  }
  
  if (intervals.length < 3) {
    return { tempo: 120, confidence: 0 };
  }
  
  // Build histogram of BPM values
  const bpmHistogram = {};
  const bpmTolerance = 2; // Group within 2 BPM
  
  for (const interval of intervals) {
    // Check beat and half-beat intervals
    for (const multiplier of [1, 2, 0.5]) {
      const bpm = Math.round(60 / (interval * multiplier));
      if (bpm >= 60 && bpm <= 200) {
        const quantizedBpm = Math.round(bpm / bpmTolerance) * bpmTolerance;
        bpmHistogram[quantizedBpm] = (bpmHistogram[quantizedBpm] || 0) + 1;
      }
    }
  }
  
  // Find most common BPM
  let maxCount = 0;
  let bestBpm = 120;
  
  for (const [bpm, count] of Object.entries(bpmHistogram)) {
    if (count > maxCount) {
      maxCount = count;
      bestBpm = parseInt(bpm);
    }
  }
  
  // Calculate confidence based on histogram consistency
  const totalVotes = Object.values(bpmHistogram).reduce((a, b) => a + b, 0);
  const confidence = totalVotes > 0 ? maxCount / totalVotes : 0;
  
  return { tempo: bestBpm, confidence };
};

/**
 * Refine beat positions based on estimated tempo
 */
const refineBeatGrid = (peaks, tempo, sampleRate) => {
  const beatInterval = 60 / tempo; // Seconds per beat
  const beats = [];
  
  if (peaks.length === 0) return beats;
  
  // Start from first strong peak
  const sortedPeaks = [...peaks].sort((a, b) => b.strength - a.strength);
  const firstBeat = sortedPeaks[0].time;
  
  // Generate beat grid
  const tolerance = beatInterval * 0.15; // 15% tolerance
  
  // Go backwards from first beat
  let beatTime = firstBeat;
  while (beatTime > 0) {
    beats.unshift(beatTime);
    beatTime -= beatInterval;
  }
  
  // Go forwards
  beatTime = firstBeat + beatInterval;
  while (beatTime < peaks[peaks.length - 1].time + beatInterval) {
    beats.push(beatTime);
    beatTime += beatInterval;
  }
  
  return beats;
};

/**
 * Analyze multiple regions of audio for more accurate tempo detection
 */
export const analyzeAudioRegions = async (audioBuffer, audioContext, numRegions = 4) => {
  const regionDuration = Math.min(10, audioBuffer.duration / numRegions); // Max 10 seconds per region
  const results = [];
  
  for (let i = 0; i < numRegions; i++) {
    const startTime = i * regionDuration;
    const regionBuffer = extractRegion(audioBuffer, audioContext, startTime, regionDuration);
    
    if (regionBuffer) {
      const result = await detectTempo(regionBuffer, audioContext);
      results.push(result);
    }
  }
  
  // Find most consistent tempo across regions
  if (results.length === 0) {
    return { tempo: 120, confidence: 0, beats: [] };
  }
  
  // Weighted average by confidence
  let totalWeight = 0;
  let weightedSum = 0;
  
  for (const result of results) {
    weightedSum += result.tempo * result.confidence;
    totalWeight += result.confidence;
  }
  
  const avgTempo = totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 120;
  const avgConfidence = totalWeight / results.length;
  
  // Use beats from highest confidence region
  const bestResult = results.reduce((best, curr) => 
    curr.confidence > best.confidence ? curr : best, results[0]);
  
  return {
    tempo: avgTempo,
    confidence: avgConfidence,
    beats: bestResult.beats,
    regionResults: results,
  };
};

/**
 * Extract a region from an audio buffer
 */
const extractRegion = (audioBuffer, audioContext, startTime, duration) => {
  const sampleRate = audioBuffer.sampleRate;
  const startSample = Math.floor(startTime * sampleRate);
  const numSamples = Math.floor(duration * sampleRate);
  
  if (startSample + numSamples > audioBuffer.length) {
    return null;
  }
  
  const regionBuffer = audioContext.createBuffer(
    1, // Mono for analysis
    numSamples,
    sampleRate
  );
  
  const sourceData = audioBuffer.getChannelData(0);
  const destData = regionBuffer.getChannelData(0);
  
  for (let i = 0; i < numSamples; i++) {
    destData[i] = sourceData[startSample + i];
  }
  
  return regionBuffer;
};

/**
 * Smart Tempo Analysis Result UI Helper
 */
export const getTempoConfidenceLabel = (confidence) => {
  if (confidence >= 0.8) return { label: 'High', color: 'text-emerald-400' };
  if (confidence >= 0.5) return { label: 'Medium', color: 'text-amber-400' };
  return { label: 'Low', color: 'text-rose-400' };
};

/**
 * Suggest tempo based on common music genres
 */
export const suggestTempoRange = (detectedTempo) => {
  const ranges = [
    { name: 'Hip-Hop/Lo-Fi', min: 70, max: 100 },
    { name: 'Pop/R&B', min: 100, max: 130 },
    { name: 'House/Dance', min: 120, max: 130 },
    { name: 'Techno/EDM', min: 130, max: 150 },
    { name: 'Drum & Bass', min: 160, max: 180 },
  ];
  
  return ranges.find(r => detectedTempo >= r.min && detectedTempo <= r.max) || null;
};

export default {
  detectTempo,
  analyzeAudioRegions,
  getTempoConfidenceLabel,
  suggestTempoRange,
};
